import { AloftPrice, isDefined } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';

import {
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';

import { MarketTrendsHelperColumn, MarketTrendsHelpers } from './types.ts';

type ChartProps = {
  width?: number;
  height?: number;
  interactive: boolean;
  marketTrendsHelpers: MarketTrendsHelpers;
};

const Y_ROUNDING = 100_000;

export const MarketTrendsChart = ({
  width,
  height,
  interactive,
  marketTrendsHelpers: { table, adjref },
}: ChartProps) => {
  const periodsBeforeProjection = new Set(
    ...[table.map((x, i) => (table[i + 1]?.shadowed ? x.period : undefined))].filter(isDefined),
  );

  const renderTooltip = (props: TooltipContentProps<number, string>) => {
    const { active, payload } = props;
    const { payload: col } = (payload[0] as { payload?: MarketTrendsHelperColumn }) ?? {};
    if (!active || col === undefined) return null;

    const { period, numcloses, p50closeprice, adjpercent, comps } = col;

    return (
      <Flex
        flexDirection="column"
        padding="1rem"
        backgroundColor="surface1"
        borderColor="strokeDefault"
        borderWidth="1px"
        borderStyle="solid"
      >
        <Typography variant="h4">{period}</Typography>
        {p50closeprice !== undefined && (
          <Typography variant="body1">
            Median Close Price: {p50closeprice.toString(true)}
          </Typography>
        )}
        {numcloses !== undefined && (
          <Typography variant="body1">Num. Closed Sales: {numcloses.toFixed(0)}</Typography>
        )}
        {adjpercent !== undefined && (
          <Typography variant="body1">
            Date of Sale Adj: {`${(adjpercent * 100).toFixed(2)}%`}
          </Typography>
        )}
        {comps.length > 0 && (
          <Typography variant="body1">Comps Affected: {comps.join(', ')}</Typography>
        )}
      </Flex>
    );
  };

  return (
    <ResponsiveContainer width={width ?? '100%'} height={height ?? 350}>
      <ComposedChart data={table} height={height ?? 350}>
        <CartesianGrid />
        <XAxis dataKey="period" fontSize="70%" />
        <YAxis
          tickFormatter={(x) => AloftPrice.fromDollarLike(x).toAbbrString()}
          fontSize="70%"
          domain={([y0, y1]) => [
            Math.floor(y0 / Y_ROUNDING) * Y_ROUNDING,
            Math.ceil(y1 / Y_ROUNDING) * Y_ROUNDING,
          ]}
        />
        {interactive && <Tooltip content={renderTooltip} isAnimationActive={false} />}
        <Line
          type="monotone"
          dataKey={(x) => (x.shadowed ? undefined : x.p50closeprice?.toDollarLike())}
          name="Median Close Price"
          isAnimationActive={false}
        />
        {adjref !== undefined && (
          <ReferenceLine y={adjref.toDollarLike()} strokeDasharray="3 3">
            <Label value="Median at effective date" position="insideBottomRight" fontSize="70%" />
          </ReferenceLine>
        )}
        <Line
          type="monotone"
          dataKey={(x) =>
            x.shadowed || periodsBeforeProjection.has(x.period)
              ? x.p50closeprice?.toDollarLike()
              : undefined
          }
          name="Projection"
          stroke="#C05621"
          strokeDasharray="3 3"
          isAnimationActive={false}
        />
        {table.map((x) =>
          x.comps.length > 0 ? (
            <ReferenceLine key={x.period} x={x.period} stroke="red" strokeDasharray="3 3">
              <Label value={x.comps.join(', ')} position="insideBottomLeft" fontSize="70%" />
            </ReferenceLine>
          ) : undefined,
        )}
        <Legend />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
